import { defineStore } from 'pinia'
import * as userApi from '../services/api/user'

export const USER_ROLE = [
  'super_admin',
  'director',
  'purchasing_supervisor',
  'accountant',
  'purchasing_admin',
  'purchasing_employee',
  'bank_admin',
  'sales_admin',
  'sales_employee',
  'other_employee',
  'marketing_manager',
  'customer_service',
  'marketing_employee',
  'inspection_manager',
  'assessor_employee',
  'service_manager',
  'service_adviser',
  'manager',
  'leader',
  'employee',
  'interior_employee',
  'garage_owner',
  'warehouseman',
  'adviser',
  'new_car_supervisor',
  'new_car_admin',
  'new_car_sales_employee',
]

export const USER_ROLE_USED_CAR = [
  'super_admin',
  'director',
  'accountant',
  'bank_admin',
  'purchasing_supervisor',
  'purchasing_admin',
  'purchasing_employee',
  'sales_admin',
  'sales_employee',
  'inspection_manager',
  'service_manager',
  'service_adviser',
  'marketing_manager',
  'customer_service',
  'garage_owner',
]

export const USER_ROLE_NEW_CAR = [
  'super_admin',
  'director',
  'accountant',
  'bank_admin',
  'marketing_manager',
  'customer_service',
  'garage_owner',
  'inspection_manager',
  'new_car_supervisor',
  'new_car_admin',
  'new_car_sales_employee',
]

export interface IApproval {
  priority: number
  user_id: number
  role: string
  status: string
  reason: string
  signature: object
  sign_at: string
}

export interface ICompletion {
  priority: number
  user_id: number
  role: string
  reason: string
  is_completed: boolean
  completed_at: string
}

export interface IStaffData {
  id: number
  avatar: any
  signature: any
  employee_code: string
  fullname: string
  phone: string
  email: string
  role: string
  is_active: boolean
  created_at: string
}

export const useStaffStore = defineStore('Staff', {
  state: () => ({
    isLoading: true,
    user: {
      id: 0,
      avatar: null,
      signature: null,
      employee_code: '',
      fullname: '',
      phone: '',
      email: '',
      role: '',
      is_active: true,
      created_at: '',
    } as IStaffData,
    users: [] as IStaffData[],
    staffs: [] as IStaffData[],
  }),
  actions: {
    setUser(data: IStaffData) {
      this.user = data
    },
    setUsers(data: IStaffData[]) {
      this.users = data
    },
    setStaffs(data: IStaffData[]) {
      this.staffs = data
    },
    setLoading(value: boolean) {
      this.isLoading = value
    },
    deleteUser(id: number) {
      this.users = this.users.filter((user) => user.id !== id)
    },
    addUser(user: IStaffData) {
      this.users.unshift(user)
    },
    async getStaffDetail(user_id: number) {
      try {
        const { data } = await userApi.get(user_id)
        this.setUser(data)
      } catch (e: unknown) {
        throw new Error(String(e))
      }
    },
    async updateStaffDetail(payload: any) {
      try {
        const { data } = await userApi.update(this.user.id, payload)
        this.setUser(data)
      } catch (e: unknown) {
        throw new Error(String(e))
      }
    },
    async listStaffs() {
      try {
        this.setLoading(true)
        const result = await userApi.listStaff()
        const { data } = result as any
        this.setStaffs(data)
        this.setLoading(false)
        return {
          ...result,
          data: data,
        }
      } catch (e: unknown) {
        throw new Error(String(e))
      }
    },
    async listUsers(params?: any) {
      try {
        this.setLoading(true)
        const result = await userApi.list(params)
        const { data } = result as any
        this.setUsers(data)
        this.setLoading(false)
        return {
          ...result,
          data: data,
        }
      } catch (e: unknown) {
        throw new Error(String(e))
      }
    },
    async deleteStaff(id: number) {
      try {
        await userApi.destroy(id)
        this.deleteUser(id)
      } catch (e: unknown) {
        throw new Error(String(e))
      }
    },
    async createStaff(payload: any) {
      try {
        const { data } = await userApi.create(payload)
        this.addUser(data)
        return data
      } catch (e: unknown) {
        throw new Error(String(e))
      }
    },
  },
})
